import { KycPrivData } from "./KycPrivForm.schema";
import { KycPrivMode } from "./KycPrivForm.types";

/** Map clean schema data -> contact.cgProfiling key names. */
export function flattenKycPriv(
  data: KycPrivData,
  mode: KycPrivMode = "combined"
): Record<string, unknown> {
  const zipCity = (zip: string, city: string) =>
    [zip, city].filter(Boolean).join(" ");

  /** For PDF AcroForm fields — no prefix, just the raw custom text. */
  const resolveOther = (val: string, custom: string | undefined) =>
    val === "other" ? custom ?? "" : val;

  /**
   * For contact.cgProfiling.* profile fields — stores "CUSTOM-<text>" so the
   * form can distinguish a custom entry from a standard option on reload.
   */
  const resolveProfileOther = (val: string, custom: string | undefined) =>
    val === "other" ? `CUSTOM-${custom ?? ""}` : val;

  const base: Record<string, unknown> = {
    // -- Personopplysninger (contact model fields)
    conName: data.personName,
    conId: data.personId,
    conAddress: data.address,
    conZipCity: zipCity(data.zip, data.city),
    conCountry: data.taxCountry,
    conCitizenship: data.nationality,
    conPhone: data.phone,
    conMail: data.email,
  };

  // -- KYC fields
  if (mode !== "mifid") {
    Object.assign(base, {
      "contact.cgProfiling.amlContactPerson": data.personName,
      pep: resolveOther(data.pep, data.pepCustom),

      "contact.cgProfiling.amlEmploymentSituation": resolveProfileOther(
        data.amlEmploymentSituation,
        data.amlEmploymentSituationCustom
      ),
      "contact.cgProfiling.amlPurposeOfCustomerRelationship":
        resolveProfileOther(
          data.amlPurposeOfCustomerRelationship,
          data.amlPurposeOfCustomerRelationshipCustom
        ),
      "contact.cgProfiling.amlInvestedPortfolio": data.amlInvestedPortfolio,
      "contact.cgProfiling.amlAnnualDeposits": data.amlAnnualDeposits,
      "contact.cgProfiling.amlSourceOfFunds": resolveProfileOther(
        data.amlSourceOfFunds,
        data.amlSourceOfFundsCustom
      ),
      "contact.cgProfiling.amlSourceOfWealth": resolveProfileOther(
        data.amlSourceOfWealth,
        data.amlSourceOfWealthCustom
      ),
      "contact.cgProfiling.amlManagedOnBehalfOfOthers": resolveProfileOther(
        data.amlManagedOnBehalfOfOthers,
        data.amlManagedOnBehalfOfOthersCustom
      ),
      "contact.cgProfiling.amlTransfersAbroad": resolveProfileOther(
        data.amlTransfersAbroad,
        data.amlTransfersAbroadCustom
      ),
      "contact.cgProfiling.amlCryptoCurrency": data.amlCryptoCurrency,
    });
  }

  // -- MIFID fields
  if (mode !== "kyc") {
    Object.assign(base, {
      "contact.cgProfiling.mifidKnowledgeOfMutualFunds":
        data.mifidKnowledgeOfMutualFunds,
      "contact.cgProfiling.mifidExperienceOfMutualFunds":
        data.mifidExperienceOfMutualFunds,
      "contact.cgProfiling.mifidTradingFrequencyOfMutualFunds":
        data.mifidTradingFrequencyOfMutualFunds,
      "contact.cgProfiling.mifidPeriodOfMutualFunds":
        data.mifidPeriodOfMutualFunds,
      "contact.cgProfiling.mifidKnowledgeOfListedStocks":
        data.mifidKnowledgeOfListedStocks,
      "contact.cgProfiling.mifidExperienceOfListedStocks":
        data.mifidExperienceOfListedStocks,
      "contact.cgProfiling.mifidTradingFrequencyOfListedStocks":
        data.mifidTradingFrequencyOfListedStocks,
      "contact.cgProfiling.mifidPeriodOfListedStocks":
        data.mifidPeriodOfListedStocks,
      "contact.cgProfiling.mifidKnowledgeOfUnlistedStocks":
        data.mifidKnowledgeOfUnlistedStocks,
      "contact.cgProfiling.mifidExperienceOfUnlistedStocks":
        data.mifidExperienceOfUnlistedStocks,
      "contact.cgProfiling.mifidTradingFrequencyOfUnlistedStocks":
        data.mifidTradingFrequencyOfUnlistedStocks,
      "contact.cgProfiling.mifidPeriodOfUnlistedStocks":
        data.mifidPeriodOfUnlistedStocks,
      "contact.cgProfiling.mifidKnowledgeOfBonds": data.mifidKnowledgeOfBonds,
      "contact.cgProfiling.mifidExperienceOfBonds": data.mifidExperienceOfBonds,
      "contact.cgProfiling.mifidTradingFrequencyOfBonds":
        data.mifidTradingFrequencyOfBonds,
      "contact.cgProfiling.mifidPeriodOfBonds": data.mifidPeriodOfBonds,
      "contact.cgProfiling.mifidKnowledgeOfComplexInvestments":
        data.mifidKnowledgeOfComplexInvestments,
      "contact.cgProfiling.mifidExperienceOfComplexInvestments":
        data.mifidExperienceOfComplexInvestments,
      "contact.cgProfiling.mifidTradingFrequencyOfComplexInvestments":
        data.mifidTradingFrequencyOfComplexInvestments,
      "contact.cgProfiling.mifidPeriodOfComplexInvestments":
        data.mifidPeriodOfComplexInvestments,
      "contact.cgProfiling.mifidRelevantExperienceEducation":
        data.mifidRelevantExperienceEducation,

      "contact.cgProfiling.mifidTotalNetWealth": data.mifidTotalNetWealth,
      "contact.cgProfiling.mifidLiquidInvestableWealth":
        data.mifidLiquidInvestableWealth,
      "contact.cgProfiling.mifidGrossAnnualIncome": data.mifidGrossAnnualIncome,
      "contact.cgProfiling.mifidLiquidityBuffer": data.mifidLiquidityBuffer,
      "contact.cgProfiling.mifidDebtBurden": data.mifidDebtBurden,
      "contact.cgProfiling.mifidIncomeStability": data.mifidIncomeStability,
      "contact.cgProfiling.mifidEarliestWithdrawal":
        data.mifidEarliestWithdrawal,

      "contact.cgProfiling.mifidMainInvestmentPurpose":
        data.mifidMainInvestmentPurpose,
      "contact.cgProfiling.mifidRiskToleranceVolatility":
        data.mifidRiskToleranceVolatility,
      "contact.cgProfiling.mifidMarketDropResponse":
        data.mifidMarketDropResponse,
      "contact.cgProfiling.mifidLiquidityNeed": data.mifidLiquidityNeed,
      "contact.cgProfiling.mifidValueDeclineImpact":
        data.mifidValueDeclineImpact,

      "contact.cgProfiling.mifidEsgPreference": data.mifidEsgPreference,
      ...(data.mifidEsgPreference === "Ja" && {
        "contact.cgProfiling.mifidEsgPreferenceType":
          data.mifidEsgPreferenceType,
      }),
    });
  }

  return base;
}
